import * as React from 'react';
import { Suspense } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Button,
  Skeleton,
  Alert,
  AlertDescription,
  AlertTitle,
} from '@forgenetwork/ui';
import { api } from '@/lib/api';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import {
  Users,
  CreditCard,
  TrendingUp,
  DollarSign,
  ArrowRight,
  Activity,
  HeartPulse,
  BarChart3,
  AlertCircle,
} from 'lucide-react';
import { AdminLayout } from '@/components/admin/AdminLayout';

export const Route = createFileRoute('/admin/')({
  component: () => (
    <ErrorBoundary>
      <Suspense fallback={<AdminDashboardSkeleton />}>
        <AdminDashboardPage />
      </Suspense>
    </ErrorBoundary>
  ),
});

interface AdminStats {
  totalUsers: number;
  activeSubscriptions: number;
  monthlyRevenue: number;
  newUsersThisMonth: number;
}

function AdminDashboardPage(): React.JSX.Element {
  // Fetch stats from multiple endpoints
  const { data: overviewData, isLoading: overviewLoading, error: overviewError } = useQuery({
    queryKey: ['admin', 'metrics', 'overview'],
    queryFn: async () => {
      const response = await api.get<{
        activeUsers: { count: number; timeframe: string };
        system: {
          totalUsers: number;
          totalWorkspaces: number;
          totalRepositories: number;
          totalAnalyses: number;
        };
      }>('/admin/metrics/overview?timeframe=30d');
      if (!response || typeof response !== 'object') {
        throw new Error('Invalid response format from metrics API');
      }
      return response;
    },
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });

  const { data: subscriptionsData, isLoading: subscriptionsLoading, error: subscriptionsError } = useQuery({
    queryKey: ['admin', 'subscriptions', 'stats'],
    queryFn: async () => {
      const response = await api.get<{
        subscriptions: Array<{ status: string; amount: number }>;
        total: number;
      }>('/admin/subscriptions?page=1&pageSize=1000');
      if (!response || typeof response !== 'object') {
        throw new Error('Invalid response format from subscriptions API');
      }
      if (!Array.isArray(response.subscriptions)) {
        throw new Error('Subscriptions data is not an array');
      }
      return response;
    },
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });

  const isLoading = overviewLoading || subscriptionsLoading;
  const hasError = overviewError || subscriptionsError;

  // Calculate stats from fetched data with null safety
  const stats: AdminStats | null = overviewData && subscriptionsData
    ? {
        totalUsers: overviewData.system?.totalUsers ?? 0,
        activeSubscriptions: Array.isArray(subscriptionsData.subscriptions)
          ? subscriptionsData.subscriptions.filter((s) => s?.status === 'active').length
          : 0,
        monthlyRevenue: Array.isArray(subscriptionsData.subscriptions)
          ? subscriptionsData.subscriptions
              .filter((s) => s?.status === 'active')
              .reduce((sum, s) => sum + (s?.amount || 0), 0)
          : 0,
        newUsersThisMonth: overviewData.activeUsers?.count ?? 0,
      }
    : null;

  const statCards = [
    {
      title: 'Total Users',
      value: stats?.totalUsers ?? 0,
      icon: Users,
      description: `+${stats?.newUsersThisMonth ?? 0} this month`,
      href: '/admin/users',
      color: 'text-blue-500',
    },
    {
      title: 'Active Subscriptions',
      value: stats?.activeSubscriptions ?? 0,
      icon: CreditCard,
      description: 'Currently active',
      href: '/admin/subscriptions',
      color: 'text-green-500',
    },
    {
      title: 'Monthly Revenue',
      value: `$${((stats?.monthlyRevenue ?? 0) / 100).toFixed(2)}`,
      icon: DollarSign,
      description: 'This month',
      href: '/admin/subscriptions',
      color: 'text-purple-500',
    },
    {
      title: 'Growth Rate',
      value: stats?.newUsersThisMonth
        ? `+${Math.round((stats.newUsersThisMonth / Math.max(stats.totalUsers - stats.newUsersThisMonth, 1)) * 100)}%`
        : '0%',
      icon: TrendingUp,
      description: 'User growth',
      href: '/admin/users',
      color: 'text-orange-500',
    },
  ];

  return (
    <AdminLayout>
      <div className="space-y-8">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Admin Dashboard</h1>
          <p className="text-muted-foreground mt-2">
            Overview of your platform metrics and quick access to admin tools
          </p>
        </div>

        {/* Error State */}
        {hasError && !isLoading && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Error Loading Dashboard</AlertTitle>
            <AlertDescription>
              {overviewError instanceof Error
                ? overviewError.message
                : subscriptionsError instanceof Error
                  ? subscriptionsError.message
                  : 'Failed to load dashboard statistics. Please try refreshing the page.'}
            </AlertDescription>
          </Alert>
        )}

        {/* Stats Grid */}
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
          {statCards.map((stat) => {
            const Icon = stat.icon;
            return (
              <Card key={stat.title} className="hover:shadow-md transition-shadow">
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">
                    {stat.title}
                  </CardTitle>
                  <Icon className={cn('h-4 w-4', stat.color)} />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">
                    {isLoading ? (
                      <span className="inline-block h-8 w-16 bg-muted animate-pulse rounded" />
                    ) : hasError ? (
                      <span className="text-muted-foreground">—</span>
                    ) : (
                      stat.value
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    {stat.description}
                  </p>
                  <Link to={stat.href}>
                    <Button variant="link" className="p-0 h-auto mt-2" size="sm" disabled={!!hasError}>
                      View details
                      <ArrowRight className="ml-1 h-3 w-3" />
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Quick Actions */}
        <div className="grid gap-6 md:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle>Quick Actions</CardTitle>
              <CardDescription>Common administrative tasks</CardDescription>
            </CardHeader>
            <CardContent className="space-y-2">
              <Link to="/admin/health">
                <Button variant="outline" className="w-full justify-start">
                  <HeartPulse className="mr-2 h-4 w-4" />
                  System Health
                </Button>
              </Link>
              <Link to="/admin/analytics">
                <Button variant="outline" className="w-full justify-start">
                  <BarChart3 className="mr-2 h-4 w-4" />
                  Analytics Dashboard
                </Button>
              </Link>
              <Link to="/admin/users">
                <Button variant="outline" className="w-full justify-start">
                  <Users className="mr-2 h-4 w-4" />
                  Manage Users
                </Button>
              </Link>
              <Link to="/admin/subscriptions">
                <Button variant="outline" className="w-full justify-start">
                  <CreditCard className="mr-2 h-4 w-4" />
                  View Subscriptions
                </Button>
              </Link>
              <Link to="/admin/activity">
                <Button variant="outline" className="w-full justify-start">
                  <Activity className="mr-2 h-4 w-4" />
                  Activity Log
                </Button>
              </Link>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Recent Activity</CardTitle>
              <CardDescription>Latest platform events</CardDescription>
            </CardHeader>
            <CardContent>
              <RecentActivityFeed />
            </CardContent>
          </Card>
        </div>
      </div>
    </AdminLayout>
  );
}

function cn(...classes: (string | undefined | null | false)[]): string {
  return classes.filter(Boolean).join(' ');
}

/**
 * Recent Activity Feed Component
 * Fetches real activity data from audit logs
 */
function RecentActivityFeed(): React.JSX.Element {
  const { data: auditLogs, isLoading } = useQuery({
    queryKey: ['admin', 'audit-logs', 'recent'],
    queryFn: async () => {
      return api.get<{
        logs: Array<{
          id: string;
          action: string;
          targetType: string;
          timestamp: string;
          admin: { name: string; email: string };
        }>;
      }>('/admin/audit-logs?page=1&pageSize=5');
    },
  });

  if (isLoading) {
    return (
      <div className="space-y-3 text-sm">
        {[1, 2, 3].map((i) => (
          <div key={i} className="flex items-start gap-3 pb-3 border-b">
            <div className="rounded-full bg-muted p-2">
              <Activity className="h-4 w-4 text-muted-foreground" />
            </div>
            <div className="flex-1">
              <div className="h-4 w-32 bg-muted rounded mb-1" />
              <div className="h-3 w-24 bg-muted rounded" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (!auditLogs?.logs || auditLogs.logs.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground text-sm">
        No recent activity
      </div>
    );
  }

  const getActivityIcon = (action: string) => {
    if (action.includes('user')) return Users;
    if (action.includes('subscription')) return CreditCard;
    return Activity;
  };

  const getActivityColor = (action: string) => {
    if (action.includes('user')) return 'bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-400';
    if (action.includes('subscription')) return 'bg-green-100 dark:bg-green-900 text-green-600 dark:text-green-400';
    return 'bg-purple-100 dark:bg-purple-900 text-purple-600 dark:text-purple-400';
  };

  const formatAction = (action: string) => {
    return action
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (c) => c.toUpperCase());
  };

  const formatTimeAgo = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
  };

  return (
    <div className="space-y-3 text-sm">
      {auditLogs.logs.map((log) => {
        const Icon = getActivityIcon(log.action);
        const colorClass = getActivityColor(log.action);
        return (
          <div key={log.id} className="flex items-start gap-3 pb-3 border-b last:border-0">
            <div className={`rounded-full p-2 ${colorClass}`}>
              <Icon className="h-4 w-4" />
            </div>
            <div className="flex-1">
              <p className="font-medium">{formatAction(log.action)}</p>
              <p className="text-muted-foreground text-xs">
                {formatTimeAgo(log.timestamp)}
              </p>
            </div>
          </div>
        );
      })}
    </div>
  );
}

/**
 * Admin Dashboard Skeleton Loader
 */
function AdminDashboardSkeleton(): React.JSX.Element {
  return (
    <AdminLayout>
      <div className="space-y-8">
        <div>
          <Skeleton className="h-9 w-64 mb-2" />
          <Skeleton className="h-5 w-96" />
        </div>
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
          {[1, 2, 3, 4].map((i) => (
            <Card key={i}>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-4 w-4" />
              </CardHeader>
              <CardContent>
                <Skeleton className="h-8 w-20 mb-2" />
                <Skeleton className="h-3 w-32" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </AdminLayout>
  );
}
